import { TFunction } from 'i18next';
import { cloneDeep, isEmpty, merge } from 'lodash';
import $$auth from 'models/auth';
import $$ep from 'models/ep';
import { $global } from 'models/store-global';

import { CurrentState } from 'components/QuestionsEngine/QuestionsEngine';

import env, { EEnvs } from 'constants/env';

import EstatePlanService from 'services/EstatePlanService';
import ExpressionsService from 'services/ExpressionsService';
import LoopService from 'services/LoopService';
import { QuestionnaireProgress } from 'services/SectionsService';
import TempUsersService from 'services/TempUsersService';

import { Answers } from 'types/zod/answers';
import { type Question } from 'types/zod/question';
import { type Widget } from 'types/zod/widget';

export const CONTINUE = 'continue';

export default class QuestionsService {
    expressionsService = ExpressionsService;

    isSpecialField = (key: string): boolean => {
        return !!key && key.startsWith('_');
    };

    getOptionsForRadioOrCheck = (question: Question, answers: Answers, skipConditions = false): Question[] => {
        return this.getInnerQuestions(question, answers, skipConditions);
    };

    getQuestionsForPage = (question: Question, answers: Answers): Question[] => {
        return this.getInnerQuestions(question, answers);
    };

    getAssessmentGroups = (question: Question, answers: Answers): Question[] => {
        const questions = this.getInnerQuestions(question, answers);
        return questions.filter((q) => (q._widget || {}).type === 'group');
    };

    enhanceQuestionWithPathsAndNames = (question: Question, fromLoop = false): Question => {
        let currentSection = '';
        let currentSubSection = '';

        const enhanceInner = (question: Question, setFullPath: boolean) => {
            if (Array.isArray(question) && question.length > 0) {
                enhanceInner(question[0] as Question, setFullPath);
            }
            Object.keys(question).forEach((key) => {
                if (!this.isSpecialField(key) || key === '_loop') {
                    if (question[key] === null) {
                        // console.log(question, key);
                    }
                    if (!question[key]._fullPath && question[key]._path) {
                        // @ts-ignore
                        question[key]._fullPath = question[key]._path.slice();
                    }
                    // @ts-ignore
                    question[key]._path = (question._path || []).slice();
                    // @ts-ignore
                    question[key]._path.push(key);
                    if (setFullPath) {
                        // @ts-ignore
                        question[key]._fullPath = question[key]._path.slice();
                    }
                    // @ts-ignore
                    question[key]._name = key;

                    if (question[key]._section) {
                        if (currentSection !== question[key]._section) {
                            question[key]._isSectionStart = true;
                        }
                        currentSection = question[key]._section as string;
                    } else {
                        if (currentSection) {
                            question[key]._section = currentSection;
                        }
                    }

                    if (question[key]._subsection) {
                        currentSubSection = question[key]._subsection as string;
                    } else {
                        if (currentSubSection) {
                            question[key]._subsection = currentSubSection;
                        }
                    }

                    enhanceInner(question[key] as Question, setFullPath);
                }
            });
        };

        enhanceInner(question, !fromLoop);

        const enhanceWithContext = (question: Question, currentFullContext: string[]) => {
            if (Array.isArray(question) && question.length > 0) {
                enhanceWithContext(question[0] as Question, currentFullContext);
            }

            const newFullContext = currentFullContext.slice();
            if (question._context) {
                newFullContext.push(question._context);
            }
            question._fullContext = newFullContext.slice();

            Object.keys(question).forEach((key) => {
                if (!this.isSpecialField(key) || key === '_loop') {
                    enhanceWithContext(question[key], newFullContext.slice());
                }
            });
        };

        if (!fromLoop) {
            enhanceWithContext(question, []);
        }

        return question;
    };

    getAnswers = (answers: Answers, question: Question): Object => {
        const widget = (question._widget || {}) as Widget;
        if (widget.type === 'table') {
            const source = widget.source;
            if (source) {
                return this.getAnswersByRelativePath(answers, source.split('.'), question._path as string[]);
            }
        }
        return this.getAnswersByPath(answers, question._path as string[]);
    };

    getAnswersByPath = (answers: Answers, path: string[] = []): Object => {
        if (!path || path.length === 0) return {};

        let result = answers;
        for (let i = 0; i < path.length; i++) {
            result = result[path[i]] as any;
            if (result === undefined) {
                return result;
            }
            if (Array.isArray(result)) {
                return result;
            }
        }
        return result;
    };

    deleteAnswers = (answers: Answers, question: Question) => {
        const widget = (question._widget || {}) as Widget;
        if (widget.type === 'table') {
            return;
        }
        this.deleteAnswersByPath(answers, question._path as string[]);
    };

    deleteAnswersByPath = (answers: Answers, path: string[]) => {
        if (!path || path.length === 0) return {};

        let result = answers;
        for (let i = 0; i < path.length - 1; i++) {
            result = result[path[i]] as any;
            if (result === undefined) {
                return;
            }
            if (Array.isArray(result)) {
                return;
            }
        }
        delete result[path[path.length - 1]];
    };

    private getAnswersByRelativePath = (answers: Answers, path: string[], fullPath: string[]): Object => {
        const currentPathCopy = fullPath.slice();
        while (currentPathCopy.length > 0) {
            currentPathCopy.pop();
            const answer = this.getAnswersByPath(answers, currentPathCopy);
            if (answer && Object.keys(answer).indexOf(path[0]) > -1) {
                return this.getAnswersByPath(answer, path);
            }
        }

        return this.getAnswersByPath(answers, path);
    };

    setAnswersByPath = (answers: Answers, path: string[], newAnswers: Answers, question?: Question) => {
        if (!path || path.length === 0) return {};

        let result = answers;
        for (let i = 0; i < path.length - 1; i++) {
            if (result[path[i]] === undefined) {
                result[path[i]] = {};
            }
            result = result[path[i]] as any;
        }

        if (typeof result !== 'object') {
            result = {};
        }

        const key = path[path.length - 1];
        if (
            question &&
            question._widget &&
            ['radio', 'check', 'toggle', 'roundcheck', 'page', 'assessment', 'productselection', 'participant'].indexOf(
                (question._widget as Widget).type,
            ) > -1
        ) {
            result[key] = newAnswers;
        } else {
            if (result[key] === undefined) {
                result[key] = cloneDeep(newAnswers);
            } else if (newAnswers !== Object(newAnswers)) {
                result[key] = newAnswers;
            } else {
                const newAnswersCopy = cloneDeep(newAnswers);
                result[key] = merge(result[key], newAnswersCopy);

                if (result[key]._loop && newAnswersCopy._loop) {
                    result[key]._loop = cloneDeep(newAnswersCopy._loop);
                }

                if (result[key]._summary && newAnswersCopy._summary) {
                    result[key]._summary = cloneDeep(newAnswersCopy._summary);
                }

                Object.keys(result[key]).forEach((name) => {
                    if (
                        newAnswersCopy[name] &&
                        question &&
                        question[name] &&
                        question[name]._widget &&
                        ['radio', 'check', 'toggle', 'roundcheck'].indexOf((question[name]._widget as Widget).type) > -1
                    ) {
                        result[key][name] = cloneDeep(newAnswersCopy[name]);
                    }
                });
            }
        }
    };

    isTerminalQuestion = (question: Question) => {
        return !Object.keys(question).find((key: string) => !this.isSpecialField(key));
    };

    checkCondition = (question: Question, answers: Answers, scopeReplacements?: any) => {
        const condition = (question._on as string) || '';
        if (!condition) {
            return true;
        }

        const scope = this.calculateExtendedScope(answers, question, scopeReplacements);

        const result = !!this.expressionsService.evaluate(condition, scope);
        if ([EEnvs.dev, EEnvs.devii, EEnvs.qa].includes(env.REACT_APP_ENV) && process.env.NODE_ENV !== `development`) {
            console.log('Result:', result, 'Condition:', condition, 'Scope:', scope);
        }
        return result;
    };

    evaluateExpression = (expression: string, question: Question, answers: Answers) => {
        const scope = this.calculateExtendedScope(answers, question);
        return this.expressionsService.evaluate(expression, scope);
    };

    processTemplate = (expression: string, question: Question, answers: Answers) => {
        const scope = this.calculateExtendedScope(answers, question);
        return this.expressionsService.processTemplate(expression, scope);
    };

    private calculateExtendedScope = (answers: Answers, question: Question, scopeReplacements?: any) => {
        const scope = this.calculateAnswersScope(answers, (question._fullPath || question._path || []) as string[]);

        if (scopeReplacements) {
            Object.keys(scopeReplacements).forEach((key) => {
                scope[key] = scopeReplacements[key];
            });
        }

        if (question._fullContext && question._fullContext.length) {
            question._fullContext.forEach((context) => (scope[context] = {}));

            let current = scope[question._fullContext[0]];
            for (let i = 1; i < question._fullContext.length; i++) {
                current[question._fullContext[i]] = {};
                current = current[question._fullContext[i]];
            }
        }

        scope.spouse = this.expressionsService.evaluate(
            'initial_questions.specify_spouse_in_ep.yes_choice.spouse_partner_name._participant',
            scope,
        );
        if (scope.ep) {
            scope.ep.spouse = this.expressionsService.evaluate(
                'ep.initial_questions.specify_spouse_in_ep.yes_choice.spouse_partner_name._participant',
                scope,
            );
        }

        return scope;
    };

    calculateAnswersScope = (answers: Answers, path: string[]) => {
        let scope = { ...answers };
        let tempScope = scope;

        for (let i = 0; i < path.length; i++) {
            if (path[i] === '_loop') break;
            tempScope = tempScope[path[i]];
            if (tempScope === undefined) {
                break;
            }
            scope = Object.assign(scope, tempScope);
        }

        const loopIndex = path.indexOf('_loop');
        if (loopIndex > -1 && path.length > loopIndex + 2) {
            scope = this.calculateAnswersScope(scope, path.slice(loopIndex + 2));
        }

        return scope;
    };

    getChildQuestions = (question: Question, answers: Answers) => {
        return this.getInnerQuestions(question, answers).filter((q) => {
            return (((q as Question)._widget || {}) as Widget).child;
        });
    };

    private getInnerQuestions = (question: Question, answers: Answers, skipConditions = false) => {
        const innerQuestions = [] as Question[];
        Object.keys(question).forEach((key) => {
            if (!this.isSpecialField(key)) {
                innerQuestions.push(question[key]);
            }
        });
        return skipConditions
            ? innerQuestions
            : innerQuestions.filter((innerQuestion) => this.checkCondition(innerQuestion, answers));
    };

    getQuestionByPath = (questionnaire: Question, path: string[], currentPath: string[]) => {
        const currentPathCopy = currentPath.slice();
        while (currentPathCopy.length > 0) {
            currentPathCopy.pop();
            const question = this.getQuestionByExactPath(questionnaire, currentPathCopy);
            if (question && Object.keys(question).indexOf(path[0]) > -1) {
                return this.getQuestionByExactPath(question, path);
            }
        }

        return this.getQuestionByExactPath(questionnaire, path);
    };

    private getQuestionByExactPath = (questionnaire: Question, path: string[]) => {
        let result = questionnaire;
        for (let i = 0; i < path.length; i++) {
            result = result[path[i]] as any;
            if (result === undefined) {
                return;
            }
        }
        return result;
    };

    getQuestionSubset = (question: Question, subset: string[]): Question => {
        if (subset.length === 0) {
            return question;
        }

        return this.getQuestionSubsetInner(question, subset);
    };

    private getQuestionSubsetInner = (question: Question, subset: string[]): Question => {
        // const result = cloneDeep(question) || {}; // This is a dirty hack to fix MAP-
        const result = cloneDeep(question);

        Object.keys(result).forEach((key) => {
            if (this.isSpecialField(key)) {
                return;
            }
            const path = (result[key]._path as string[]) || [];
            if (path.length && !this.isPathMatches(path, subset)) {
                delete result[key];
            }
        });

        Object.keys(result).forEach((key) => {
            if (this.isSpecialField(key)) {
                return;
            }
            result[key] = this.getQuestionSubsetInner(result[key] as Question, subset);
        });

        return result;
    };

    private isPathMatches = (longPath: string[], subset: string[]) => {
        for (let i = 0; i < subset.length; i++) {
            if (subset[i].endsWith('*')) {
                if (this.checkWildcardPathMatch(longPath, subset[i].split('.'))) {
                    return true;
                }
            } else {
                if (this.isPathMatchesInner(longPath, subset[i].split('.'))) {
                    return true;
                }
            }
        }
        return false;
    };

    private isPathMatchesInner = (longPath: string[], shortPath: string[]): boolean => {
        if (shortPath.length > longPath.length) {
            return false;
        }

        const positionToStartComparison = longPath.length - shortPath.length;
        for (let i = 0; i < shortPath.length; i++) {
            if (longPath[positionToStartComparison + i] !== shortPath[i]) {
                return false;
            }
        }
        return true;
    };

    private checkWildcardPathMatch = (path: string[], wildcardPath: string[]) => {
        const pathString = path.join('.');
        let wildcardPathString = wildcardPath.join('.');
        wildcardPathString = wildcardPathString.substring(0, wildcardPathString.length - 2);
        return pathString.endsWith('.' + wildcardPathString) || pathString.includes('.' + wildcardPathString + '.');
    };

    isValidAnswer = (
        question: Question,
        answer: Answers,
        answersForConditions: Answers,
        questionnaire?: Question,
    ): boolean => {
        const widget = (question._widget || {}) as Widget;
        const widgetType = widget.type;
        if (!widgetType && answer === undefined) {
            return false;
        }
        switch (widgetType) {
            case 'article':
            case 'autofill':
            case 'diagram':
            case 'personaldata':
                return !!answer;

            case 'assessment':
                return $$ep.$selectedProduct.map(Boolean).getState();

            case 'numeric':
                if (widget.require !== false && (answer === undefined || answer === null)) {
                    return false;
                }
                return !(
                    widget.min_value &&
                    answer !== undefined &&
                    answer !== null &&
                    Number(answer) < widget.min_value
                );

            case 'date':
                if (widget.require !== false) {
                    return answer !== undefined && answer !== '' && answer !== true;
                }
                return true;

            case 'money': {
                if (widget.require !== false) {
                    return answer !== undefined && answer !== '' && answer !== null && answer !== true && answer !== 0;
                }
                return true;
            }

            case 'textarea':
                if (answer === undefined) {
                    return false;
                }
                return !(widget.require !== false && (answer === '' || answer === true));

            case 'text':
                if (answer === undefined) {
                    return false;
                }
                if (widget.require !== false && (answer === '' || answer === true)) {
                    return false;
                }
                if (widget.validation_regex) {
                    return new RegExp(widget.validation_regex, widget.validation_regex_flags || '').test(answer);
                }
                return true;

            case 'daterange':
                return answer !== undefined && answer.from && (!answer.to || answer.to >= answer.from);

            case 'radio': {
                if (!answer) {
                    return false;
                }
                const key = Object.keys(answer)[0];
                const value = answer[key];
                const optionQuestion = this.getOptionsForRadioOrCheck(question, answersForConditions).find(
                    (q) => q._name === key,
                );
                if (!optionQuestion) {
                    return true;
                } else {
                    return this.isValidAnswer(optionQuestion, value, answersForConditions, questionnaire);
                }
            }

            case 'check': {
                if (!answer) return true;
                const keys = Object.keys(answer);
                return !keys.find((key) => {
                    const value = answer[key];
                    const optionQuestion = this.getOptionsForRadioOrCheck(question, answersForConditions).find(
                        (q) => q._name === key,
                    );
                    if (!optionQuestion) {
                        return false;
                    } else {
                        return !this.isValidAnswer(optionQuestion, value, answersForConditions, questionnaire);
                    }
                });
            }

            case 'label': {
                const childQuestions = this.getChildQuestions(question, answersForConditions);
                if (childQuestions.length === 0) {
                    return true;
                }
                return !childQuestions.find((q) => {
                    return !this.isValidAnswer(q, answer[q._name as string], answersForConditions, questionnaire);
                });
            }

            case 'participant':
                return !!answer;

            case 'table': {
                if (!answer?._loop || answer?._loop.length < (widget.min_size || 1)) {
                    return false;
                }
                if (widget.inline_edit && widget.inline_total_required) {
                    const sum = new LoopService().roundTo2Decimals(
                        (answer._loop as any[])
                            .filter((a) => a !== null)
                            .reduce(
                                (a, b) =>
                                    a +
                                    (this.getAnswersByPath(
                                        b,
                                        this.processTemplate(widget.inline_edit!, {}, b).split('.'),
                                    ) || 0),
                                0,
                            ),
                    );
                    return sum === widget.inline_total_required;
                }
                if (!questionnaire) {
                    return true;
                }
                const loopQuestion = this.getLoopQuestion(question, questionnaire);
                for (const loopAnswer of answer._loop) {
                    const { isFinished } = this.getQuestionnaireProgress(
                        loopQuestion,
                        questionnaire,
                        loopAnswer,
                        answersForConditions,
                    );
                    if (!isFinished) {
                        return false;
                    }
                }
                return true;
            }

            case 'signup': {
                const email = $$auth.$currentParticipant.map((p) => p?.email || '').getState();
                return !TempUsersService.isTempUser(email);
            }

            case 'productselection':
                return !isEmpty($$ep.$selectedProduct.getState());

            case 'orientation':
                return !!answer;

            case 'payment': {
                const qInstance = answersForConditions.q_instance;
                const id = qInstance.questionnaire_instance_id;
                return (
                    (qInstance.questionnaire_type_code === 'EP' && EstatePlanService.isToBePayedAtTheEnd()) ||
                    $global.map((state) => state.isPayed?.[id] || state.isRightAfterPayment?.[id] || false).getState()
                );
            }

            case 'dropdown':
                return !!answer;

            case 'changestatus':
                return answersForConditions.q_instance.status === widget.status;
        }

        return true;
    };

    getPathInsideLoop = (path: string[]) => {
        const loopPathIndex = path.lastIndexOf('_loop');
        if (loopPathIndex === -1) {
            return path.slice();
        }
        return path.slice(loopPathIndex + 2);
    };

    public getResourceKey = (
        question: Question,
        namespace: string,
        keyOverride?: string,
        suffix?: string,
        context?: string[],
        pseudoContext?: string,
    ) => {
        let key = keyOverride ? keyOverride : ((question._widget || {}) as Widget).format || question._name;
        if (suffix) {
            key += '_' + suffix;
        }
        let contextString = (context || []).join('.');
        if (contextString) {
            contextString += '.';
        }
        if (pseudoContext) {
            contextString = contextString + pseudoContext + '.';
        }
        return `${namespace}:${contextString}${key}`;
    };

    getQuestionId = (question: Question, answers?: any) => {
        let fullId = (((question || {})._miro || {}) as any).id;
        if (!fullId) return '';

        if (answers) {
            fullId = this.expressionsService.processTemplate(fullId, this.calculateExtendedScope(answers, question));
        }

        const dashPosition = fullId.indexOf('-');
        if (dashPosition > -1) {
            return fullId.substring(dashPosition + 1);
        } else {
            return fullId;
        }
    };

    addChildQuestions = (nextQuestion: Question, nextState: CurrentState) => {
        const questionKeys = Object.keys(nextQuestion);
        if (
            ['radio', 'check', 'toggle', 'page', 'assessment'].indexOf(((nextQuestion._widget as Widget) || {}).type) <
            0
        ) {
            questionKeys.reverse().forEach((key) => {
                if (!this.isSpecialField(key)) {
                    this.addFutureQuestionToStack(nextQuestion, key, nextState);
                }
            });
        }
    };

    private addFutureQuestionToStack = (nextQuestion: Question, key: string, nextState: CurrentState) => {
        // @ts-ignore
        if (Array.isArray(nextQuestion[key]) && (nextQuestion[key] as Array<Question>).length > 0) {
            // @ts-ignore
            const arr = nextQuestion[key] as Array<Question>;
            if (arr.length > 0) {
                nextState.futureQuestionsStack.push(arr[0]);
            }
            return;
        }
        const widget = ((nextQuestion[key] as Question)._widget || {}) as Widget;
        if (widget.child || widget.type === 'skip') {
            return;
        }

        nextState.futureQuestionsStack.push(nextQuestion[key]);
    };

    getQuestionLabel = (
        t: TFunction,
        question: Question,
        documentType: string,
        answersForConditions: Answers,
        suffix?: string,
        defaultValue?: string,
        keyOverride?: string,
    ) => {
        const scope = this.calculateExtendedScope(answersForConditions, question);
        const template = this.getTemplate(t, question, documentType, suffix, defaultValue, keyOverride);
        return this.expressionsService.processTemplate(template, scope);
    };

    public getTemplate = (
        t: TFunction,
        question: Question,
        documentType: string,
        suffix?: string,
        defaultValue?: string,
        keyOverride?: string,
    ) => {
        let context = (question._fullContext || []).slice();
        const productContext = EstatePlanService.getSelectedProductContext();
        let template = '';
        const NOT_FOUND_VALUE = 'NOT_FOUND_VALUE';
        while (context.length >= 0) {
            template = t(
                this.getResourceKey(question, documentType, keyOverride, suffix, context, productContext),
                NOT_FOUND_VALUE,
                {
                    interpolation: { prefix: '{{{{', suffix: '}}}}' },
                },
            );
            if (template !== NOT_FOUND_VALUE) {
                return template;
            }
            if (context.length === 0) {
                break;
            }
            context.pop();
        }

        context = (question._fullContext || []).slice();

        while (context.length > 0) {
            template = t(this.getResourceKey(question, documentType, keyOverride, suffix, context), NOT_FOUND_VALUE, {
                interpolation: { prefix: '{{{{', suffix: '}}}}' },
            });
            if (template !== NOT_FOUND_VALUE) {
                return template;
            }
            context.pop();
        }

        return t(this.getResourceKey(question, documentType, keyOverride, suffix), defaultValue, {
            interpolation: { prefix: '{{{{', suffix: '}}}}' },
        });
    };

    getQuestionSubLabel = (t: TFunction, question: Question, documentType: string, answersForConditions: Answers) => {
        return this.getQuestionLabel(t, question, documentType, answersForConditions, 'sub', '');
    };

    disallowConcurrency = (fn: Function) => {
        let inProgressPromise = Promise.resolve();

        return async (...args: any) => {
            await inProgressPromise;
            inProgressPromise = inProgressPromise
                .then(() => fn(...args))
                .catch((e: any) => {
                    inProgressPromise = Promise.resolve();
                    throw e;
                });

            return inProgressPromise;
        };
    };

    getQuestionnaireProgress = (
        questionnaire: Question,
        fullQuestionnaire: Question,
        answers: Answers,
        commonAnswers: Answers,
    ): QuestionnaireProgress => {
        let isStarted = false;
        let isFinished = false;

        const questionnaireCopy = Object.assign({}, questionnaire);
        delete questionnaireCopy.update;

        const currentState = {
            question: {} as Question,
            futureQuestionsStack: [questionnaireCopy] as Question[],
        };

        let currentValue = null as any;

        const answersForConditions = merge(cloneDeep(commonAnswers), answers);

        while (true) {
            if (
                ((currentState.question._widget || {}) as Widget).type === 'radio' &&
                this.checkCondition(currentState.question, answersForConditions)
            ) {
                if (currentValue) {
                    const answerKey = Object.keys(currentValue)[0];
                    const answer = currentState.question[answerKey] || {};
                    const answerWidget = ((answer as Question)._widget || {}) as Widget;
                    if (!answerWidget.child) {
                        this.addChildQuestions(answer as Question, currentState);
                    }
                }
            }

            if (currentState.futureQuestionsStack.length === 0) {
                isFinished = true;
                break;
            }

            const nextQuestion = currentState.futureQuestionsStack.pop() as Question;
            if (this.checkCondition(nextQuestion, answersForConditions)) {
                this.addChildQuestions(nextQuestion, currentState);
            }

            currentState.question = nextQuestion;

            currentValue = this.getAnswers(answers, currentState.question);
            if (currentState.question._path && currentState.question._widget && currentValue) {
                isStarted = true;
            }

            const hiddenByCondition = !this.checkCondition(currentState.question, answersForConditions);

            if (hiddenByCondition) continue;

            if (!this.isValidAnswer(currentState.question, currentValue, answersForConditions, fullQuestionnaire)) {
                break;
            }
        }

        return {
            isStarted,
            isFinished,
        };
    };

    getLoopQuestion = (question: any, questionnaire: any) => {
        const widget = (question._widget || {}) as Widget;
        const source = widget.source;
        const subset = widget.subset || [];

        let q = cloneDeep(
            (
                ((source
                    ? this.getQuestionByPath(questionnaire, source.split('.'), question._path as string[]) || {}
                    : question
                )._loop || []) as Question[]
            )[0],
        ) as Question;

        q = this.getQuestionSubset(q, subset);

        q._path = [] as string[];
        this.enhanceQuestionWithPathsAndNames(q, true);

        return q;
    };

    isOnlyParticipantQuestion = (loopQuestion: Question, answers: Answers) => {
        let firstQuestion = null;
        for (const [key, value] of Object.entries(loopQuestion || {})) {
            if (this.isSpecialField(key)) {
                continue;
            }
            if (firstQuestion === null) {
                firstQuestion = value;
                if (firstQuestion._widget?.type !== 'participant') {
                    return false;
                }
            } else {
                if (this.checkCondition(value, answers)) {
                    return false;
                }
            }
        }

        if (!firstQuestion) {
            return false;
        }

        for (const [key, value] of Object.entries((firstQuestion || {}) as Question)) {
            if (this.isSpecialField(key)) {
                continue;
            }
            if (this.checkCondition(value, answers)) {
                return false;
            }
        }

        return true;
    };
}
